import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import MainNavigation from '../components/common/MainNavigation';
import ScrollReveal from '../components/common/ScrollReveal';
import TeslaButton from '../components/common/TeslaButton';
import HeroImageCarousel from '../components/common/HeroImageCarousel';
import DeferredSrcImage from '../components/common/DeferredSrcImage';
import SiteFooter from '../components/common/SiteFooter';
import { SERVICE_IMAGES } from '../constants/serviceImages';
import StaticPageSeo from '../seo/StaticPageSeo';

const ServicesPage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredServices = activeCategory === 'all' 
    ? services 
    : services.filter(service => service.category === activeCategory);

  return (
    <>
      <StaticPageSeo path="/services" />
      {/* Main Navigation */}
      <MainNavigation variant="solid" />
      
      <div className="services-page">
        {/* Hero Section */}
        <section className="section section--hero section--dark">
          <div className="container">
            <div className="row align-items-center min-vh-100">
              <div className="col-lg-6">
                <ScrollReveal animation="fade-in-up">
                  <h1 className="text-heading-1 mb-6 text-white">
                    Professional Beauty Services
                  </h1>
                  <p className="text-body-large mb-8 text-gray-40">
                    Discover a wide range of beauty services provided by our verified professionals. 
                    From haircuts to makeup, nails to skincare - we've got you covered.
                  </p>
                  <Link to="/register" style={{ textDecoration: 'none' }}>
                    <TeslaButton variant="primary" size="lg">
                      Book Your Service
                    </TeslaButton>
                  </Link>
                </ScrollReveal>
              </div>
              <div className="col-lg-6">
                <ScrollReveal animation="fade-in-left" delay={200}>
                  <div className="hero-image" style={{ position: 'relative', height: '500px', overflow: 'hidden', borderRadius: '12px' }}>
                    <HeroImageCarousel ariaLabel="Professional beauty services" />
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>

        {/* Service Categories Filter */}
        <section className="section section--light">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <ScrollReveal animation="fade-in-up">
                  <div className="service-filters mb-12">
                    <div className="d-flex justify-content-center flex-wrap gap-3">
                      {categories.map((category) => (
                        <button
                          key={category.id}
                          className={`tesla-button ${
                            activeCategory === category.id 
                              ? 'tesla-button--primary' 
                              : 'tesla-button--outline'
                          }`}
                          onClick={() => setActiveCategory(category.id)}
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </ScrollReveal>
              </div>
            </div>

            {/* Services Grid */}
            <div className="row">
              {filteredServices.map((service, index) => {
                // Special handling for images that need different positioning
                const getObjectPosition = (serviceId: number) => {
                  return 'center';
                };
                
                return (
                <div key={service.id} className="col-lg-4 col-md-6 mb-8">
                  <ScrollReveal animation="fade-in-up" delay={100 * (index % 6)}>
                    <div className="tesla-card h-100">
                      <div className="service-card">
                        <div className="service-card__image" style={{ height: '500px', overflow: 'hidden' }}>
                          <DeferredSrcImage
                            src={service.image}
                            alt={service.name}
                            className="img-fluid"
                            draggable={false}
                            onContextMenu={(event) => event.preventDefault()}
                            style={{ 
                              width: '100%', 
                              height: '100%', 
                              objectFit: 'cover',
                              objectPosition: getObjectPosition(service.id)
                            }}
                          />
                          <div className="service-card__category">
                            {service.categoryName}
                          </div>
                        </div>
                        <div className="service-card__content p-6">
                          <h3 className="text-heading-3 mb-3">{service.name}</h3>
                          <p className="text-body-medium mb-4">{service.description}</p>
                          
                          <div className="service-card__details mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span className="text-body-small text-gray-60">Duration:</span>
                              <span className="fw-medium">{service.duration}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span className="text-body-small text-gray-60">Starting from:</span>
                              <span className="fw-medium text-accent">${service.price}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="text-body-small text-gray-60">Popularity:</span>
                              <div className="d-flex">
                                {[...Array(5)].map((_, i) => (
                                  <span key={i} className={i < service.rating ? "text-warning" : "text-gray-40"}>★</span>
                                ))}
                              </div>
                            </div>
                          </div>

                          <div className="service-card__features mb-6">
                            <h4 className="text-heading-4 mb-3">What's Included:</h4>
                            <ul className="service-features-list">
                              {service.features.map((feature, idx) => (
                                <li key={idx} className="text-body-small mb-1">
                                  <span className="text-accent mr-2">✓</span>
                                  {feature}
                                </li>
                              ))}
                            </ul>
                          </div>

                          <Link 
                            to="/browse" 
                            style={{ textDecoration: 'none' }}
                            className="d-block"
                          >
                            <TeslaButton variant="primary" size="md" className="w-100">
                              Book Now
                            </TeslaButton>
                          </Link>
                        </div>
                    </div>
                  </div>
                </ScrollReveal>
              </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Mobile Services Section */}
        <section className="section section--center">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6 mb-8 mb-lg-0">
                <ScrollReveal animation="fade-in-right">
                  <div style={{ height: '500px', overflow: 'hidden', borderRadius: '12px' }}>
                    <DeferredSrcImage
                      src={SERVICE_IMAGES.mobileServices}
                      alt="Mobile beauty services - Professional stylist at client's home"
                      className="img-fluid"
                      draggable={false}
                      onContextMenu={(event) => event.preventDefault()}
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover',
                        objectPosition: 'center'
                      }}
                    />
                  </div>
                </ScrollReveal>
              </div>
              <div className="col-lg-6">
                <ScrollReveal animation="fade-in-left">
                  <h2 className="text-heading-2 mb-4">Mobile Services Available</h2>
                  <p className="text-body-medium mb-8" style={{ color: 'var(--color-text-secondary, #666)' }}>
                    Can't make it to a salon? No problem! Many of our services are available as mobile services,
                    bringing professional beauty care directly to your location.
                  </p>

                  <div className="row g-4 mb-8">
                    {mobileServiceBenefits.map((benefit, index) => (
                      <div key={index} className="col-12">
                        <ScrollReveal animation="fade-in-up" delay={150 * index}>
                          <div
                            className="tesla-card d-flex align-items-center p-5"
                            style={{
                              borderRadius: '16px',
                              border: '1px solid rgba(0,0,0,0.06)',
                              boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
                              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                              gap: '1.25rem',
                            }}
                            onMouseEnter={e => {
                              (e.currentTarget as HTMLDivElement).style.boxShadow = '0 6px 28px rgba(0,0,0,0.12)';
                              (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={e => {
                              (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 16px rgba(0,0,0,0.06)';
                              (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                            }}
                          >
                            <div
                              style={{
                                width: '56px',
                                height: '56px',
                                borderRadius: '14px',
                                background: 'linear-gradient(135deg, #f5f0ff 0%, #ede9fe 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1.75rem',
                                flexShrink: 0,
                              }}
                            >
                              {benefit.icon}
                            </div>
                            <div>
                              <h4 className="text-heading-4 mb-1" style={{ fontWeight: 600 }}>{benefit.title}</h4>
                              <p className="text-body-medium mb-0" style={{ color: 'var(--color-text-secondary, #666)' }}>
                                {benefit.description}
                              </p>
                            </div>
                          </div>
                        </ScrollReveal>
                      </div>
                    ))}
                  </div>

                  <Link to="/register" style={{ textDecoration: 'none' }}>
                    <TeslaButton variant="outline" size="lg">
                      Book Mobile Service
                    </TeslaButton>
                  </Link>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>

        {/* Hair Braiding Section */}
        <section className="section section--light">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6 order-lg-2 mb-8 mb-lg-0">
                <ScrollReveal animation="fade-in-left">
                  <div style={{ height: '500px', overflow: 'hidden', borderRadius: '12px', backgroundColor: '#f0f0f0' }}>
                    <DeferredSrcImage
                      src={SERVICE_IMAGES.expertHairBraiding}
                      alt="Professional hair braiding — stylist working with a client"
                      className="img-fluid"
                      draggable={false}
                      onContextMenu={(event) => event.preventDefault()}
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover',
                        objectPosition: 'center'
                      }}
                    />
                  </div>
                </ScrollReveal>
              </div>
              <div className="col-lg-6 order-lg-1">
                <ScrollReveal animation="fade-in-right">
                  <h2 className="text-heading-2 mb-6">Expert Hair Braiding Services</h2>
                  <p className="text-body-medium mb-6">
                    Experience the artistry of professional hair braiding. Our skilled stylists specialize in 
                    creating beautiful, long-lasting braided styles for all hair types and textures.
                  </p>
                  
                  <div className="mb-8">
                    <h4 className="text-heading-4 mb-4">What We Offer:</h4>
                    <ul className="service-features-list">
                      <li className="text-body-medium mb-2">
                        <span className="text-accent mr-2">✓</span>
                        Box braids, cornrows, and twist styles
                      </li>
                      <li className="text-body-medium mb-2">
                        <span className="text-accent mr-2">✓</span>
                        Protective styling for healthy hair growth
                      </li>
                      <li className="text-body-medium mb-2">
                        <span className="text-accent mr-2">✓</span>
                        Custom designs and patterns
                      </li>
                      <li className="text-body-medium mb-2">
                        <span className="text-accent mr-2">✓</span>
                        Professional consultation and styling advice
                      </li>
                    </ul>
                  </div>

                  <Link to="/register" style={{ textDecoration: 'none' }}>
                    <TeslaButton variant="primary" size="lg">
                      Book Braiding Service
                    </TeslaButton>
                  </Link>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="section section--light">
          <div className="container">
            <div className="row">
              <div className="col-lg-8 mx-auto text-center mb-12">
                <ScrollReveal animation="fade-in-up">
                  <h2 className="text-heading-2 mb-6">How It Works</h2>
                  <p className="text-body-large">
                    Booking your beauty service is simple and straightforward.
                  </p>
                </ScrollReveal>
              </div>
            </div>
            
            <div className="row">
              {howItWorksSteps.map((step, index) => (
                <div key={index} className="col-lg-3 col-md-6 mb-8">
                  <ScrollReveal animation="fade-in-up" delay={200 * index}>
                    <div className="text-center">
                      <div className="step-icon mb-4">
                        <div className="step-number">{step.number}</div>
                        <div className="step-emoji" style={{ fontSize: '3rem' }}>
                          {step.icon}
                        </div>
                      </div>
                      <h3 className="text-heading-3 mb-4">{step.title}</h3>
                      <p className="text-body-medium">{step.description}</p>
                    </div>
                  </ScrollReveal>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section section--dark">
          <div className="container text-center">
            <ScrollReveal animation="fade-in-up">
              <h2 className="text-heading-2 mb-6 text-white">Ready to Look and Feel Your Best?</h2>
              <p className="text-body-large mb-8 text-gray-40">
                Join thousands of satisfied clients who trust LookBel for their beauty needs.
              </p>
              <div className="d-flex justify-content-center gap-4">
                <Link to="/register" style={{ textDecoration: 'none' }}>
                  <TeslaButton variant="primary" size="lg">
                    Book Your Service
                  </TeslaButton>
                </Link>
                <Link to="/about" style={{ textDecoration: 'none' }}>
                  <TeslaButton variant="outline" size="lg">
                    Learn More
                  </TeslaButton>
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </section>

        <SiteFooter />
      </div>
    </>
  );
};

// Service categories
const categories = [
  { id: 'all', name: 'All Services' },
  { id: 'hair', name: 'Hair' },
  { id: 'makeup', name: 'Makeup' },
  { id: 'nails', name: 'Nails' },
  { id: 'skincare', name: 'Skincare' },
  { id: 'massage', name: 'Massage' }
];

// Services data
const services = [
  {
    id: 1,
    name: 'Professional Haircut & Style',
    category: 'hair',
    categoryName: 'Hair',
    description: 'Expert haircut and styling tailored to your face shape and lifestyle.',
    duration: '60-90 mins',
    price: '45',
    rating: 5,
    image: SERVICE_IMAGES.professionalHaircut,
    features: ['Consultation', 'Wash & Cut', 'Professional Styling', 'Product Recommendations']
  },
  {
    id: 2,
    name: 'Hair Color & Highlights',
    category: 'hair',
    categoryName: 'Hair',
    description: 'Transform your look with professional hair coloring and highlighting services.',
    duration: '2-4 hours',
    price: '85',
    rating: 5,
    image: SERVICE_IMAGES.hairColor,
    features: ['Color Consultation', 'Professional Application', 'Toning & Finishing', 'Aftercare Advice']
  },
  {
    id: 3,
    name: 'Bridal Makeup',
    category: 'makeup',
    categoryName: 'Makeup',
    description: 'Flawless bridal makeup that lasts all day and photographs beautifully.',
    duration: '90-120 mins',
    price: '120',
    rating: 5,
    image: SERVICE_IMAGES.bridalMakeup,
    features: ['Trial Session', 'Premium Products', 'Long-lasting Formula', 'Touch-up Kit']
  },
  {
    id: 4,
    name: 'Special Event Makeup',
    category: 'makeup',
    categoryName: 'Makeup',
    description: 'Professional makeup for special occasions, parties, and photo shoots.',
    duration: '60-90 mins',
    price: '65',
    rating: 4,
    image: SERVICE_IMAGES.specialEventMakeup,
    features: ['Custom Look Design', 'High-quality Products', 'False Lashes', 'Setting Spray']
  },
  {
    id: 5,
    name: 'Gel Manicure',
    category: 'nails',
    categoryName: 'Nails',
    description: 'Long-lasting gel manicure with perfect finish and chip-resistant formula.',
    duration: '45-60 mins',
    price: '35',
    rating: 5,
    image: SERVICE_IMAGES.gelManicure,
    features: ['Nail Shaping', 'Cuticle Care', 'Base & Top Coat', '2-week Guarantee']
  },
  {
    id: 6,
    name: 'Nail Art Design',
    category: 'nails',
    categoryName: 'Nails',
    description: 'Creative nail art designs customized to your style and preferences.',
    duration: '60-90 mins',
    price: '55',
    rating: 4,
    image: SERVICE_IMAGES.nailArtDesign,
    features: ['Custom Design', 'Premium Gel Polish', 'Hand-painted Art', 'Protective Top Coat']
  },
  {
    id: 7,
    name: 'Deep Cleansing Facial',
    category: 'skincare',
    categoryName: 'Skincare',
    description: 'Thorough facial treatment to cleanse, exfoliate, and rejuvenate your skin.',
    duration: '75-90 mins',
    price: '80',
    rating: 5,
    image: SERVICE_IMAGES.deepCleansingFacial,
    features: ['Skin Analysis', 'Deep Cleansing', 'Extraction', 'Hydrating Mask']
  },
  {
    id: 8,
    name: 'Anti-Aging Treatment',
    category: 'skincare',
    categoryName: 'Skincare',
    description: 'Advanced anti-aging facial with premium products and techniques.',
    duration: '90-120 mins',
    price: '125',
    rating: 5,
    image: SERVICE_IMAGES.antiAgingTreatment,
    features: ['Collagen Boost', 'Fine Line Reduction', 'Firming Treatment', 'Luxury Products']
  },
  {
    id: 9,
    name: 'Relaxation Massage',
    category: 'massage',
    categoryName: 'Massage',
    description: 'Full-body relaxation massage to relieve stress and tension.',
    duration: '60-90 mins',
    price: '70',
    rating: 5,
    image: SERVICE_IMAGES.relaxationMassage,
    features: ['Swedish Technique', 'Aromatherapy Oils', 'Stress Relief', 'Muscle Relaxation']
  }
];

// Mobile service benefits
const mobileServiceBenefits = [
  {
    icon: '🏠',
    title: 'Comfort of Home',
    description: 'Enjoy professional services in the comfort and privacy of your own space.'
  },
  {
    icon: '⏰',
    title: 'Save Time',
    description: 'No travel time or waiting in salons - we come to you at your convenience.'
  },
  {
    icon: '👥',
    title: 'Group Bookings',
    description: 'Perfect for bridal parties, events, or getting ready with friends.'
  }
];

// How it works steps
const howItWorksSteps = [
  {
    number: '01',
    icon: '🔍',
    title: 'Browse Services',
    description: 'Explore our wide range of beauty services and find what you need.'
  },
  {
    number: '02',
    icon: '📅',
    title: 'Book Appointment',
    description: 'Select your preferred date, time, and stylist for your service.'
  },
  {
    number: '03',
    icon: '💄',
    title: 'Get Beautiful',
    description: 'Enjoy your professional beauty service and love your new look.'
  },
  {
    number: '04',
    icon: '⭐',
    title: 'Rate & Review',
    description: 'Share your experience and help others discover great stylists.'
  }
];

export default ServicesPage; 